import { extendTheme } from "@chakra-ui/react";

const colors = {
  brand: {
    50: "#e8f5fb",
    100: "#cde7f4",
    200: "#a9d6ea",
    300: "#7fc4e0",
    400: "#49a9d0",
    500: "#0c8dc3",
    600: "#0b7da9",
    700: "#0a6a90",
    800: "#035c81",
    900: "#023f5a",
  },
  seafoam: {
    50: "#e6f7ef",
    100: "#c9ecdb",
    200: "#a8dfc5",
    300: "#85d1ae",
    400: "#61c498",
    500: "#34a270",
    600: "#2a865b",
    700: "#216a48",
    800: "#184f36",
    900: "#0a2d1d",
  },
  mint: {
    50: "#ebfaf3",
    100: "#cdf0de",
    200: "#ade5c9",
    300: "#8cdab3",
    400: "#74d1a3",
    500: "#6fc39c",
    600: "#54a57f",
    700: "#3d8060",
    800: "#2b5c44",
    900: "#153224",
  },
  neutral: {
    50: "#f5f6f5",
    100: "#eaebea",
    200: "#d7d9d7",
    300: "#c3c6c3",
    400: "#b0b3b0",
    500: "#a8aba8",
    600: "#858785",
    700: "#656665",
    800: "#444544",
    900: "#262726",
  },
  orchid: {
    50: "#f4ebf8",
    100: "#e7cff4",
    200: "#d7adeb",
    300: "#c98be2",
    400: "#ba6dd8",
    500: "#8c4e9f",
    600: "#743d84",
    700: "#5c2e69",
    800: "#43214d",
    900: "#2b1533",
  },
  lilac: {
    50: "#fbf4fd",
    100: "#f5e4f8",
    200: "#edd3f3",
    300: "#e2bcea",
    400: "#d6a1e0",
    500: "#c893d9",
    600: "#ac6dbf",
    700: "#874f96",
    800: "#60356d",
    900: "#3a2044",
  },
  fuchsia: {
    50: "#fef0fb",
    100: "#fbd3f1",
    200: "#f6a6e4",
    300: "#f079d8",
    400: "#e74dcb",
    500: "#0c8dc3",
    600: "#af2496",
    700: "#8f1b79",
    800: "#6f125d",
    900: "#48093a",
  },
};

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const styles = {
  global: {
    "html, body": {
      backgroundColor: "#ffffff",
      color: "#0f2a3c",
      fontFamily: "Inter, system-ui, sans-serif",
    },
    body: {
      minHeight: "100vh",
      backgroundImage: "none",
      backgroundAttachment: "fixed",
    },
    a: {
      color: "brand.600",
    },
    "*::selection": {
      background: "lilac.200",
      color: "brand.900",
    },
  },
};

const components = {
  Button: {
    baseStyle: {
      borderRadius: "full",
      fontWeight: "semibold",
      letterSpacing: "0.01em",
    },
    defaultProps: {
      colorScheme: "brand",
    },
    variants: {
      solid: {
        bg: "brand.500",
        color: "white",
        _hover: { bg: "brand.600" },
        _active: { bg: "brand.700" },
      },
      outline: {
        borderColor: "brand.400",
        color: "brand.600",
        _hover: { bg: "brand.50", borderColor: "brand.500" },
        _active: { bg: "brand.100", borderColor: "brand.600" },
      },
      ghost: {
        color: "brand.600",
        _hover: { bg: "brand.50" },
      },
    },
  },
  Tabs: {
    baseStyle: {
      tab: {
        fontWeight: "semibold",
      },
    },
    variants: {
      "brand-pill": {
        tab: {
          borderRadius: "full",
          color: "brand.600",
          _selected: {
            bg: "brand.500",
            color: "white",
            boxShadow: "md",
          },
          _hover: { bg: "brand.50" },
        },
        tablist: {
          gap: 2,
          bg: "rgba(255, 255, 255, 0.85)",
          p: 1,
          borderRadius: "full",
          boxShadow: "sm",
          backdropFilter: "blur(6px)",
        },
      },
    },
    defaultProps: {
      variant: "brand-pill",
      colorScheme: "brand",
    },
  },
  Link: {
    baseStyle: {
      color: "brand.600",
      fontWeight: "semibold",
      _hover: {
        color: "brand.700",
        textDecoration: "none",
      },
    },
  },
  Heading: {
    baseStyle: {
      color: "brand.800",
      fontWeight: "semibold",
    },
  },
  Input: {
    variants: {
      outline: {
        field: {
          borderRadius: "full",
          _focus: {
            borderColor: "brand.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
          },
        },
      },
    },
    defaultProps: {
      focusBorderColor: "brand.500",
    },
  },
  Select: {
    defaultProps: {
      focusBorderColor: "brand.500",
    },
  },
  Progress: {
    baseStyle: {
      track: {
        bg: "neutral.100",
      },
      filledTrack: {
        bg: "brand.500",
      },
    },
  },
  Divider: {
    baseStyle: {
      borderColor: "neutral.200",
    },
  },
};

const fonts = {
  heading: "Inter, system-ui, sans-serif",
  body: "Inter, system-ui, sans-serif",
};

const theme = extendTheme({ colors, config, styles, components, fonts });

export default theme;
